import React from 'react';
import {
  Server,
  Zap,
  Shield,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import ProgressGauge from '../../../../components/charts/ProgressGauge';
import ComplianceTrendChart from '../../../../components/charts/ComplianceTrendChart';
import SystemPerformanceChart from '../../../../components/charts/SystemPerformanceChart';
import { SystemHealthTabProps } from './types';

const SystemHealthTab: React.FC<SystemHealthTabProps> = ({
  systemHealthData,
  analytics,
  systemMetrics,
  healthChecks,
  systemPerformanceData,
  formatUptime,
  platformMetrics,
  recentAlerts,
}) => {
  return (
    <div className="space-y-6">
      {/* System Health Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center mb-3">
            <Server className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="text-2xl font-bold text-slate-900">{systemHealthData.overall}%</div>
          <div className="text-sm text-slate-600">Overall Health</div>
          <div className="text-xs text-emerald-600 mt-1">System status</div>
        </div>

        <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
            <Zap className="w-5 h-5 text-blue-600" />
          </div>
          <div className="text-2xl font-bold text-slate-900">{analytics.avgResponseTime !== null ? analytics.avgResponseTime + "ms" : "\u2014"}</div>
          <div className="text-sm text-slate-600">Avg Response</div>
          <div className="text-xs text-blue-600 mt-1">API performance</div>
        </div>

        <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center mb-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-2xl font-bold text-slate-900">{formatUptime(analytics.uptime)}</div>
          <div className="text-sm text-slate-600">System Uptime</div>
          <div className="text-xs text-green-600 mt-1">Service availability</div>
        </div>

        <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center mb-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <div className="text-2xl font-bold text-slate-900">{analytics.criticalAlertsCount}</div>
          <div className="text-sm text-slate-600">Critical Alerts</div>
          <div className="text-xs text-red-600 mt-1">Require attention</div>
        </div>
      </div>

      {/* System Health Gauges */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <ProgressGauge
            value={systemHealthData.overall}
            title="Overall Health"
            subtitle={systemHealthData.overall >= 90 ? 'All systems optimal' : systemHealthData.overall >= 70 ? 'Systems operational' : 'Attention needed'}
            color={systemHealthData.overall >= 90 ? '#10b981' : systemHealthData.overall >= 70 ? '#f59e0b' : '#ef4444'}
            size={120}
          />
        </div>
        <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <ProgressGauge
            value={platformMetrics.overallCompliance ?? 0}
            title="Compliance Rate"
            subtitle={platformMetrics.overallCompliance >= 90 ? 'Above target' : 'Below target'}
            color={platformMetrics.overallCompliance >= 90 ? '#3b82f6' : '#f59e0b'}
            size={120}
          />
        </div>
        <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <ProgressGauge
            value={systemMetrics?.apiResponseTime ? Math.max(0, 100 - systemMetrics.apiResponseTime / 10) : 0}
            title="API Performance"
            subtitle={`${systemMetrics?.apiResponseTime ?? 0}ms avg`}
            color={systemMetrics?.apiResponseTime && systemMetrics.apiResponseTime < 300 ? '#8b5cf6' : '#f59e0b'}
            size={120}
          />
        </div>
        <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <ProgressGauge
            value={systemMetrics?.errorRate ? Math.max(0, 100 - systemMetrics.errorRate * 1000) : 98}
            title="Error Rate"
            subtitle={`${(systemMetrics?.errorRate != null ? systemMetrics.errorRate * 100 : 0).toFixed(2)}%`}
            color={systemMetrics?.errorRate && systemMetrics.errorRate < 0.05 ? '#10b981' : '#ef4444'}
            size={120}
          />
        </div>
      </div>

      {/* Detailed System Health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <h4 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-600" />
            Security Status
          </h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Active Alerts</span>
              <span className={`text-sm font-medium ${recentAlerts.length > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                {recentAlerts.length} {recentAlerts.length === 1 ? 'alert' : 'alerts'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Critical Issues</span>
              <span className="text-sm font-medium text-red-600">
                {recentAlerts.filter((a: any) => a.severity === 'high' || a.severity === 'critical').length}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Health Checks</span>
              <span className="text-sm font-medium text-slate-900">
                {healthChecks.length} {healthChecks.length === 1 ? 'check' : 'checks'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">System Status</span>
              <span className={`text-sm font-medium ${systemHealthData.overall >= 90 ? 'text-green-600' : systemHealthData.overall >= 70 ? 'text-orange-600' : 'text-red-600'}`}>
                {systemHealthData.overall >= 90 ? 'Healthy' : systemHealthData.overall >= 70 ? 'Degraded' : 'Critical'}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <h4 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Server className="w-4 h-4 text-emerald-600" />
            System Resources
          </h4>
          <div className="space-y-6">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600">CPU Usage</span>
                <span className="font-medium text-slate-900">{systemMetrics?.cpuUsage ?? 0}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-2 rounded-full ${systemMetrics?.cpuUsage && systemMetrics.cpuUsage > 80 ? 'bg-red-600' : systemMetrics?.cpuUsage && systemMetrics.cpuUsage > 60 ? 'bg-orange-600' : 'bg-blue-600'}`}
                  style={{ width: `${Math.min(systemMetrics?.cpuUsage ?? 0, 100)}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600">Memory Usage</span>
                <span className="font-medium text-slate-900">{systemMetrics?.memoryUsage ?? 0}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-2 rounded-full ${systemMetrics?.memoryUsage && systemMetrics.memoryUsage > 80 ? 'bg-red-600' : systemMetrics?.memoryUsage && systemMetrics.memoryUsage > 60 ? 'bg-orange-600' : 'bg-green-600'}`}
                  style={{ width: `${systemMetrics?.memoryUsage ?? 0}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600">Storage Usage</span>
                <span className="font-medium text-slate-900">{systemMetrics?.storageUsage ?? 0}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-2 rounded-full ${systemMetrics?.storageUsage && systemMetrics.storageUsage > 80 ? 'bg-red-600' : systemMetrics?.storageUsage && systemMetrics.storageUsage > 60 ? 'bg-orange-600' : 'bg-purple-600'}`}
                  style={{ width: `${systemMetrics?.storageUsage ?? 0}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600">Database Connections</span>
                <span className="font-medium text-slate-900">{systemMetrics?.databaseConnections ?? 0}</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                <div
                  className="h-2 rounded-full bg-sky-700"
                  style={{ width: `${Math.min(100, (systemMetrics?.databaseConnections ?? 0) * 5)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* System Health Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <ComplianceTrendChart
            data={{
              labels: systemHealthData.labels || [],
              compliance: systemHealthData.trend || [],
              target: systemHealthData.trend ? systemHealthData.trend.map(() => 90) : [],
              risk: systemHealthData.trend?.map((v: number) => Math.max(0, 100 - v)) || []
            }}
            title="System Health Trends"
            height={280}
            showTarget={true}
            showRisk={true}
          />
        </div>

        {/* System Performance Chart in Health Tab */}
        {systemPerformanceData.labels.length > 0 && (
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <h4 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Zap className="w-4 h-4 text-blue-600" />
              Performance Metrics
            </h4>
            <SystemPerformanceChart
              data={systemPerformanceData}
              title=""
              height={280}
            />
          </div>
        )}
      </div>

      {/* Health Check Details */}
      <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
        <h4 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Server className="w-4 h-4 text-emerald-600" />
          Component Health Status
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {healthChecks.length > 0 ? healthChecks.slice(0, 6).map((check: any, index: number) => (
            <div key={index} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">{check.name || check.component || 'Component'}</span>
                <div className={`w-3 h-3 rounded-full ${
                  check.status === 'healthy' ? 'bg-green-500' :
                  check.status === 'degraded' ? 'bg-yellow-500' : 'bg-red-500'
                }`}></div>
              </div>
              <div className="text-xs text-slate-500 mb-2">{check.description || 'Health check'}</div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Status</span>
                <span className={`text-xs font-medium ${
                  check.status === 'healthy' ? 'text-green-600' :
                  check.status === 'degraded' ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {check.status || 'unknown'}
                </span>
              </div>
              {check.responseTime && (
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-slate-500">Response</span>
                  <span className="text-xs font-medium text-slate-700">{check.responseTime}ms</span>
                </div>
              )}
            </div>
          )) : (
            <div className="col-span-full py-8">
              <Server className="text-slate-400 mb-2" size={32} />
              <p className="text-sm text-slate-500">No health check data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemHealthTab;
